import {FieldArrayWithId} from 'react-hook-form'
import {useMaterialEditorController} from '../hooks/useMaterialEditorController'
import {BulkOrderFormValues} from '../../BulkOrdersFormValues'
import {
  ActiveMaterialDeliveryItem,
  OrderRequest,
  OrdersFormValidityState,
  QuantityType
} from '../../declarations/types'
import {useBulkOrderFormChangeHandlerWithMethods} from '../../FormChangeHandlers'
import {Box, Grid, Paper} from '@mui/material'
import {CircularProgress} from '@mui/material'
import {SplitLoadOrderSummarySection} from '../containers/SplitLoadOrderSummarySection'
import {ShippingType} from '../../declarations/OrderIntake.enums'
import {OrderSummarySection} from '../containers/OrderSummarySection'
import {MaterialOrderEditor} from '../containers/MaterialOrderEditor'
import {partial} from 'lodash'
import React, {SetStateAction} from 'react'

interface DeliverSidebarProps {
  // State
  placeOrderStatus: boolean
  noValidMaterial: boolean
  isSplitLoadMode: boolean
  isAllOrdersValid: boolean
  activePopup: string | undefined
  setActivePopup: React.Dispatch<SetStateAction<any>>

  // Material editor
  materialEditorController: ReturnType<typeof useMaterialEditorController>
  selectedMaterialIndex: number
  orderFields: FieldArrayWithId<BulkOrderFormValues, 'orders'>[]
  orders: OrderRequest[]
  isCustomerReferenceSame: boolean
  isSlotsManagementEnabled: boolean
  useConfigurableSlots: boolean
  editorActivationTimeStamp: string | undefined
  savedBomKeysFromParentForm: Record<number, string | undefined> | undefined
  selectedSplitLoadMaterialsFromParentForm: Record<number, any> | undefined
  ordersFormValidity: OrdersFormValidityState[]
  setOrdersFormValidity: (val: OrdersFormValidityState[]) => void

  // Callbacks from useFieldHandlers
  onCustomerReferenceChange: (index: number, value: string) => void
  onQuantityChange: (index: number, quantity: number) => void
  onDriverInstructionsChange: (index: number, value: string) => void
  onDispatchNotesChange: (index: number, value: string) => void
  onBomKeyChange: (index: number, value: string | undefined) => void
  onMaterialChange: (index: number, value: any) => void
  onSplitLoadMaterialsSelected: (index: number, value: any) => void
  handleStatusChange: (hasChanges: boolean) => void
  removeDelivery: (index: number) => void
  handleEditorActivation: (id: string, item: ActiveMaterialDeliveryItem, index: number) => void
  handleSummarySectionEditButtonClick: (index: number) => void

  // Form handlers
  formChangeHandlers: ReturnType<typeof useBulkOrderFormChangeHandlerWithMethods>
  customerId?: string
  quantityType: QuantityType

  //UI
  classes: any
}

export const DeliverSidebar = (props: DeliverSidebarProps) => {
  const {
    placeOrderStatus,
    noValidMaterial,
    isSplitLoadMode,
    isAllOrdersValid,
    activePopup,
    setActivePopup,
    materialEditorController,
    selectedMaterialIndex,
    orderFields,
    orders,
    isCustomerReferenceSame,
    isSlotsManagementEnabled,
    useConfigurableSlots,
    editorActivationTimeStamp,
    savedBomKeysFromParentForm,
    selectedSplitLoadMaterialsFromParentForm,
    ordersFormValidity,
    setOrdersFormValidity,
    onCustomerReferenceChange,
    onQuantityChange,
    onDriverInstructionsChange,
    onDispatchNotesChange,
    onBomKeyChange,
    onMaterialChange,
    onSplitLoadMaterialsSelected,
    handleStatusChange,
    removeDelivery,
    handleEditorActivation,
    handleSummarySectionEditButtonClick,
    formChangeHandlers,
    customerId,
    quantityType,
    classes
  } = props

  return (
    <Grid item xs={12} md={4} style={{paddingLeft: 16, paddingRight: 0}}>
      <Paper className={classes.paperStyle}>
        <div className={classes.stickyContainer}>
          {placeOrderStatus ? (
            <Box display="flex" alignItems="center" flexDirection="column">
              <CircularProgress size={24} color="inherit" />
            </Box>
          ) : materialEditorController.activeMaterialForm === undefined || noValidMaterial ? (
            isSplitLoadMode ? (
              <SplitLoadOrderSummarySection
                shippingType={ShippingType.DELIVER}
                customerId={customerId}
                quantityType={quantityType}
                onCloseSummaryBox={() => setActivePopup(undefined)}
                onCustomerRefChange={formChangeHandlers.onGlobalCustomerRefChange}
                onEmailCcChange={formChangeHandlers.onGlobalEmailCcChange}
                onEditItem={handleSummarySectionEditButtonClick}
                isAllOrdersValid={isAllOrdersValid}
                onRevalidateForm={(invalidOrders) => {
                  setOrdersFormValidity(invalidOrders)
                }}
              />
            ) : (
              <OrderSummarySection
                shippingType={ShippingType.DELIVER}
                customerId={customerId}
                quantityType={quantityType}
                onCloseSummaryBox={() => setActivePopup(undefined)}
                onCustomerRefChange={formChangeHandlers.onGlobalCustomerRefChange}
                onEmailCcChange={formChangeHandlers.onGlobalEmailCcChange}
                onEditItem={handleSummarySectionEditButtonClick}
                isAllOrdersValid={isAllOrdersValid}
                onRevalidateForm={(invalidOrders) => {
                  setOrdersFormValidity(invalidOrders)
                }}
              />
            )
          ) : (
            orderFields.length > 0 &&
            selectedMaterialIndex > -1 &&
            selectedMaterialIndex < orderFields.length && (
              <MaterialOrderEditor
                key={materialEditorController.activeMaterialForm}
                orderPayload={orderFields[selectedMaterialIndex].payload}
                canDeleteMaterial={orders.length > 1}
                isCustomerReferenceSame={isCustomerReferenceSame}
                checkValidityBySlots={isSlotsManagementEnabled && useConfigurableSlots}
                index={selectedMaterialIndex}
                shippingType={ShippingType.DELIVER}
                activeItem={materialEditorController.activeSection}
                editorMode={materialEditorController.activeMode}
                showPoTab={!isCustomerReferenceSame}
                closeActiveItem={() => materialEditorController.setActiveOrderBox(undefined)}
                onChange={() =>
                  materialEditorController.activeMaterialForm &&
                  materialEditorController.notifySuccessfullySaved(
                    materialEditorController.activeMaterialForm
                  )
                }
                onDateChange={partial(formChangeHandlers.onDateChange, selectedMaterialIndex)}
                onTimeChange={partial(formChangeHandlers.onTimeChange, selectedMaterialIndex)}
                onCustomerReferenceChange={(customerReference) =>
                  onCustomerReferenceChange(selectedMaterialIndex, customerReference)
                }
                onQuantityChange={(quantity) => onQuantityChange(selectedMaterialIndex, quantity)}
                onDriverInstructionsChange={(driverInstructions) =>
                  onDriverInstructionsChange(selectedMaterialIndex, driverInstructions)
                }
                onDispatchNotesChange={(dispatchNotes) =>
                  onDispatchNotesChange(selectedMaterialIndex, dispatchNotes)
                }
                savedBomKey={
                  savedBomKeysFromParentForm?.[selectedMaterialIndex] as string | undefined
                }
                onBomKeyChange={(bomKey: string | undefined) =>
                  onBomKeyChange(selectedMaterialIndex, bomKey)
                }
                onMaterialChange={(selectedMaterialOption) =>
                  onMaterialChange(selectedMaterialIndex, selectedMaterialOption)
                }
                onSplitLoadMaterialsSelected={(materials) =>
                  onSplitLoadMaterialsSelected(selectedMaterialIndex, materials)
                }
                savedSplitLoadData={
                  selectedSplitLoadMaterialsFromParentForm?.[selectedMaterialIndex]
                }
                isSplitLoad={isSplitLoadMode}
                onTimeSelectorTrack={partial(
                  formChangeHandlers.onTimeSelectorTrackApply,
                  selectedMaterialIndex,
                  editorActivationTimeStamp,
                  isSlotsManagementEnabled,
                  orderFields[selectedMaterialIndex].id
                )}
                onDeleteMaterial={() => removeDelivery(selectedMaterialIndex)}
                onStatusChange={handleStatusChange}
                onFormValidation={(validity: OrdersFormValidityState) => {
                  const updatedOrdersFormValidity = [...ordersFormValidity]
                  updatedOrdersFormValidity[validity.index] = validity
                  setOrdersFormValidity(updatedOrdersFormValidity)
                }}
              />
            )
          )}
        </div>
      </Paper>
    </Grid>
  )
}
